import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp, DollarSign, BarChart3, PieChart,
  ChevronDown, ChevronUp, Wallet, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(val) {
  if (!val && val !== 0) return "$0";
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

const ALLOC_COLORS = [
  { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50" },
  { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-50" },
  { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50" },
  { bg: "bg-purple-500", text: "text-purple-700", light: "bg-purple-50" },
  { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50" },
];

export default function InvestorPortfolioPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/investor/portfolio-analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <Loader2 className="h-5 w-5 text-emerald-600 animate-spin" />
    </div>
  );

  if (!data) return null;

  const kpis = [
    { icon: DollarSign, label: "Total Committed", value: formatCurrency(data.total_committed), color: "emerald" },
    { icon: BarChart3, label: "Active Deals", value: data.active_deals, color: "blue" },
    { icon: TrendingUp, label: "Projected Yield", value: data.projected_yield_pct ? `${data.projected_yield_pct}%` : (data.roi_pct ? `${data.roi_pct > 0 ? "+" : ""}${data.roi_pct}%` : "—"), color: "emerald" },
    { icon: Wallet, label: "Available", value: formatCurrency(data.available_capital), color: "amber" },
  ];

  return (
    <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.03)]" data-testid="investor-portfolio-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#FDFCF9] transition-colors"
      >
        <div className="flex items-center gap-2">
          <PieChart className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-bold text-[#0A0A0A] tracking-tight">Portfolio Overview</span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-[#bbb]" /> : <ChevronDown className="h-4 w-4 text-[#bbb]" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          {/* KPI grid */}
          <div className="grid grid-cols-2 gap-3">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="rounded-xl bg-[#FDFCF9] border border-[#f0ece4] p-3" data-testid={`portfolio-kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <kpi.icon className={`h-3 w-3 text-${kpi.color}-600`} />
                  <span className="text-[9px] uppercase tracking-[0.1em] text-[#888] font-bold">{kpi.label}</span>
                </div>
                <p className="text-lg font-bold text-[#0A0A0A] tracking-tight">{kpi.value}</p>
              </div>
            ))}
          </div>

          {/* Returns breakdown */}
          {data.total_returned > 0 && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3.5">
              <p className="text-[9px] uppercase tracking-[0.12em] text-emerald-700 font-bold mb-2">Returns</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs font-bold text-emerald-800">{formatCurrency(data.total_interest_earned)}</p>
                  <p className="text-[8px] text-emerald-600">Interest</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-800">{formatCurrency(data.total_principal_returned)}</p>
                  <p className="text-[8px] text-emerald-600">Principal</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-800">{formatCurrency(data.net_gain)}</p>
                  <p className="text-[8px] text-emerald-600">Net Gain</p>
                </div>
              </div>
            </div>
          )}

          {/* Diversification */}
          {data.diversification?.length > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-[0.12em] text-[#888] font-bold mb-2">Allocation</p>
              <div className="h-3 rounded-full bg-[#f0ece4] overflow-hidden flex">
                {data.diversification.map((d, i) => {
                  const color = ALLOC_COLORS[i % ALLOC_COLORS.length];
                  return (
                    <div
                      key={d.type}
                      className={`h-full ${color.bg} first:rounded-l-full last:rounded-r-full`}
                      style={{ width: `${d.percentage}%`, minWidth: "4px" }}
                      title={`${d.type}: ${d.percentage}%`}
                    />
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {data.diversification.map((d, i) => {
                  const color = ALLOC_COLORS[i % ALLOC_COLORS.length];
                  return (
                    <div key={d.type} className="flex items-center gap-1" data-testid={`alloc-${d.type}`}>
                      <div className={`h-2 w-2 rounded-sm ${color.bg}`} />
                      <span className="text-[9px] text-[#888]">{d.type} {d.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
